// v2.0.0 - 2026-02-01 - Member Dashboard with energy summary and TI quotes

import { useState, useEffect } from 'react'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface DashboardData {
  member: {
    id: string
    name: string
    pod: string
    memberType: string
  }
  currentPeriod: {
    year: number
    month: number
    immessaKwh: number
    prelevataKwh: number
    eaciKwh: number
    quota: number
    status: 'pending' | 'calculated' | 'paid'
  } | null
  totalEarnings: number
  totalEaci: number
  recentQuotes: Array<{
    year: number
    month: number
    eaciKwh: number
    quota: number
    status: string
  }>
}

const MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
]

export default function MemberDashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/member/dashboard')
      if (!response.ok) throw new Error('Errore nel caricamento dati')
      const result = await response.json()
      setData(result.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore')
    } finally {
      setIsLoading(false)
    }
  }

  const formatNumber = (n: number, decimals: number = 2) => {
    return n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  }

  const formatCurrency = (n: number) => `${formatNumber(n)} EUR`
  const formatKwh = (n: number) => `${formatNumber(n)} kWh`

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="default">In attesa</Badge>
      case 'calculated':
        return <Badge variant="warning">Calcolato</Badge>
      case 'paid':
        return <Badge variant="success">Pagato</Badge>
      default:
        return <Badge variant="default">{status}</Badge>
    }
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" description="La tua panoramica" />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent)]"></div>
        </div>
      </PageContainer>
    )
  }

  if (error || !data) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" description="La tua panoramica" />
        <Card>
          <CardContent className="py-12 text-center text-[var(--color-error)]">
            {error || 'Nessun dato disponibile'}
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader title={`Ciao, ${data.member.name}`} description="La tua panoramica sulla comunita energetica" />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-[var(--color-text-secondary)]">Totale Guadagni</p>
            <p className="text-2xl font-bold text-[var(--color-success)]">{formatCurrency(data.totalEarnings)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-[var(--color-text-secondary)]">EACI Totale</p>
            <p className="text-2xl font-bold">{formatKwh(data.totalEaci)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-[var(--color-text-secondary)]">Il tuo POD</p>
            <p className="text-lg font-mono">{data.member.pod}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-[var(--color-text-secondary)]">Tipo Membro</p>
            <p className="text-lg font-medium capitalize">{data.member.memberType}</p>
          </CardContent>
        </Card>
      </div>

      {/* Current Period */}
      {data.currentPeriod && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  Periodo Corrente: {MONTHS[data.currentPeriod.month - 1]} {data.currentPeriod.year}
                </CardTitle>
                <CardDescription>I tuoi dati per il periodo in corso</CardDescription>
              </div>
              {getStatusBadge(data.currentPeriod.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                <p className="text-sm text-[var(--color-text-secondary)]">Energia Immessa</p>
                <p className="text-xl font-bold text-green-500">{formatKwh(data.currentPeriod.immessaKwh)}</p>
              </div>
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                <p className="text-sm text-[var(--color-text-secondary)]">Energia Prelevata</p>
                <p className="text-xl font-bold text-blue-500">{formatKwh(data.currentPeriod.prelevataKwh)}</p>
              </div>
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                <p className="text-sm text-[var(--color-text-secondary)]">Il tuo EACI</p>
                <p className="text-xl font-bold text-amber-500">{formatKwh(data.currentPeriod.eaciKwh)}</p>
              </div>
              <div className="p-4 rounded-lg bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30">
                <p className="text-sm text-[var(--color-text-secondary)]">Quota Spettante</p>
                <p className="text-xl font-bold text-[var(--color-accent)]">
                  {formatCurrency(data.currentPeriod.quota)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Quotes */}
      <Card>
        <CardHeader>
          <CardTitle>Storico Quote</CardTitle>
          <CardDescription>Le tue quote TI degli ultimi mesi</CardDescription>
        </CardHeader>
        <CardContent>
          {data.recentQuotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 font-medium">Periodo</th>
                    <th className="text-right py-3 px-4 font-medium">EACI</th>
                    <th className="text-right py-3 px-4 font-medium">Quota</th>
                    <th className="text-center py-3 px-4 font-medium">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentQuotes.map((quote, index) => (
                    <tr key={index} className="border-b border-[var(--color-border)]">
                      <td className="py-3 px-4">
                        {MONTHS[quote.month - 1]} {quote.year}
                      </td>
                      <td className="py-3 px-4 text-right">{formatKwh(quote.eaciKwh)}</td>
                      <td className="py-3 px-4 text-right font-bold text-[var(--color-success)]">
                        {formatCurrency(quote.quota)}
                      </td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(quote.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-8 text-[var(--color-text-secondary)]">
              Nessuna quota disponibile ancora
            </p>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
